import type { CbiFrontendServiceContext } from "../../frontend/context/CbiFrontendServiceContext";
import type { ApClassSignupStaff } from "./ApClassSignupStaff";
import type { ApClassStaff } from "./ApClassStaff";
import type { ApClassTypeOverrideStaff } from "./ApClassTypeOverrideStaff";
import type { CardStaff } from "./CardStaff";
import type { DamageWaiverStaff } from "./DamageWaiverStaff";
import type { GetNameOptions } from "./GetNameOptions";
import type { JpClassSignupStaff } from "./JpClassSignupStaff";
import type { JpClassStaff } from "./JpClassStaff";
import type { JpClassTypeOverrideStaff } from "./JpClassTypeOverrideStaff";
import type { MembershipStaff } from "./MembershipStaff";
import type { PersonAddressRef } from "./PersonAddressRef";
import type { PersonAddressStaff } from "./PersonAddressStaff";
import type { PersonCheckInStaff } from "./PersonCheckInStaff";
import type { PersonDTOBase } from "./PersonDTOBase";
import type { PersonPersonTypeStaff } from "./PersonPersonTypeStaff";
import type { PersonPhoneRef } from "./PersonPhoneRef";
import type { PersonPhoneStaff } from "./PersonPhoneStaff";
import type { PersonRatingStaff } from "./PersonRatingStaff";
import type { PersonReferralStaff } from "./PersonReferralStaff";
import type { PersonRelationStaff } from "./PersonRelationStaff";
import type { PersonTypeRef } from "./PersonTypeRef";
import type { TrackedBase } from "./TrackedBase";

export class PersonStaff implements PersonDTOBase, TrackedBase {
  constructor(public readonly personId: number, public readonly personType: PersonTypeRef | null, public readonly temp: string, public readonly email: string | null, public readonly dob: Date | null, public readonly dobVerified: boolean | null, public readonly allowSignouts: boolean | null, public readonly gender: string | null, public readonly ethnicity: string | null, public readonly language: string | null, public readonly student: boolean | null, public readonly reducedLunch: boolean | null, public readonly school: string | null, public readonly military: string | null, public readonly veteranEligible: boolean | null, public readonly swimProof: number | null, public readonly sailingExp: string | null, public readonly allergies: string | null, public readonly medications: string | null, public readonly specialNeeds: string | null, public readonly employer: string | null, public readonly occupation: string | null, public readonly matchingGifts: string | null, public readonly deceased: boolean | null, public readonly badEmail: boolean | null, public readonly doNotCall: boolean | null, public readonly doNotEmail: boolean | null, public readonly doNotMail: boolean | null, public readonly referrals: Array<PersonReferralStaff> | null, public readonly damageWaivers: Array<DamageWaiverStaff> | null, public readonly phones: Array<PersonPhoneStaff> | null, public readonly personRatings: Array<PersonRatingStaff> | null, public readonly memberships: Array<MembershipStaff> | null, public readonly primaryAddressId: PersonAddressRef | null, public readonly primaryPhoneId: PersonPhoneRef | null, public readonly addresses: Array<PersonAddressStaff> | null, public readonly cards: Array<CardStaff> | null, public readonly personTypes: Array<PersonPersonTypeStaff> | null, public readonly createdOn: Date | null, public readonly createdBy: string | null, public readonly updatedOn: Date | null, public readonly updatedBy: string | null, public readonly prefix: string | null, public readonly first: string | null, public readonly middle: string | null, public readonly last: string | null, public readonly suffix: string | null, public readonly preferred: string | null, public readonly organization: string | null, public readonly name: string, public readonly tempStatus: string | null, public readonly apClassesInstructing: Array<ApClassStaff> | null, public readonly jpClassesInstructing: Array<JpClassStaff> | null, public readonly apClassSignups: Array<ApClassSignupStaff> | null, public readonly jpClassSignups: Array<JpClassSignupStaff> | null, public readonly apClassTypeOverrides: Array<ApClassTypeOverrideStaff> | null, public readonly jpClassTypeOverrides: Array<JpClassTypeOverrideStaff> | null, public readonly relationsA: Array<PersonRelationStaff> | null, public readonly relationsB: Array<PersonRelationStaff> | null, public readonly checkIns: Array<PersonCheckInStaff> | null, public readonly activeCardNum: string | null, public readonly activeMembershipTypeName: string | null, public readonly activeMembershipExpiration: Date | null, public readonly primaryPhoneNumber: string | null) {
    this.cbiServiceContext = null;
    this.referralsLoaded = this.referrals != null;
    this.referralsCached = this.referrals;
    this.damageWaiversLoaded = this.damageWaivers != null;
    this.damageWaiversCached = this.damageWaivers;
    this.phonesLoaded = this.phones != null;
    this.phonesCached = this.phones;
    this.personRatingsLoaded = this.personRatings != null;
    this.personRatingsCached = this.personRatings;
    this.membershipsLoaded = this.memberships != null;
    this.membershipsCached = this.memberships;
    this.addressesLoaded = this.addresses != null;
    this.addressesCached = this.addresses;
    this.cardsLoaded = this.cards != null;
    this.cardsCached = this.cards;
    this.personTypesLoaded = this.personTypes != null;
    this.personTypesCached = this.personTypes;
    this.apClassesInstructingLoaded = this.apClassesInstructing != null;
    this.apClassesInstructingCached = this.apClassesInstructing;
    this.jpClassesInstructingLoaded = this.jpClassesInstructing != null;
    this.jpClassesInstructingCached = this.jpClassesInstructing;
    this.apClassSignupsLoaded = this.apClassSignups != null;
    this.apClassSignupsCached = this.apClassSignups;
    this.jpClassSignupsLoaded = this.jpClassSignups != null;
    this.jpClassSignupsCached = this.jpClassSignups;
    this.apClassTypeOverridesLoaded = this.apClassTypeOverrides != null;
    this.apClassTypeOverridesCached = this.apClassTypeOverrides;
    this.jpClassTypeOverridesLoaded = this.jpClassTypeOverrides != null;
    this.jpClassTypeOverridesCached = this.jpClassTypeOverrides;
    this.relationsALoaded = this.relationsA != null;
    this.relationsACached = this.relationsA;
    this.relationsBLoaded = this.relationsB != null;
    this.relationsBCached = this.relationsB;
    this.checkInsLoaded = this.checkIns != null;
    this.checkInsCached = this.checkIns;
  }
  cbiServiceContext: CbiFrontendServiceContext | null;
  private referralsLoaded: boolean;
  private referralsCached: Array<PersonReferralStaff> | null;
  private damageWaiversLoaded: boolean;
  private damageWaiversCached: Array<DamageWaiverStaff> | null;
  private phonesLoaded: boolean;
  private phonesCached: Array<PersonPhoneStaff> | null;
  private personRatingsLoaded: boolean;
  private personRatingsCached: Array<PersonRatingStaff> | null;
  private membershipsLoaded: boolean;
  private membershipsCached: Array<MembershipStaff> | null;
  private addressesLoaded: boolean;
  private addressesCached: Array<PersonAddressStaff> | null;
  private cardsLoaded: boolean;
  private cardsCached: Array<CardStaff> | null;
  private personTypesLoaded: boolean;
  private personTypesCached: Array<PersonPersonTypeStaff> | null;
  private apClassesInstructingLoaded: boolean;
  private apClassesInstructingCached: Array<ApClassStaff> | null;
  private jpClassesInstructingLoaded: boolean;
  private jpClassesInstructingCached: Array<JpClassStaff> | null;
  private apClassSignupsLoaded: boolean;
  private apClassSignupsCached: Array<ApClassSignupStaff> | null;
  private jpClassSignupsLoaded: boolean;
  private jpClassSignupsCached: Array<JpClassSignupStaff> | null;
  private apClassTypeOverridesLoaded: boolean;
  private apClassTypeOverridesCached: Array<ApClassTypeOverrideStaff> | null;
  private jpClassTypeOverridesLoaded: boolean;
  private jpClassTypeOverridesCached: Array<JpClassTypeOverrideStaff> | null;
  private relationsALoaded: boolean;
  private relationsACached: Array<PersonRelationStaff> | null;
  private relationsBLoaded: boolean;
  private relationsBCached: Array<PersonRelationStaff> | null;
  private checkInsLoaded: boolean;
  private checkInsCached: Array<PersonCheckInStaff> | null;
  async referralsLazy(): Promise<Array<PersonReferralStaff>> {
    if (!this.referralsLoaded) {
      this.referralsCached = await this.cbiServiceContext!.personService().getPersonStaffReferrals(this.personId);
      this.referralsLoaded = true;
    }
    return this.referralsCached!;
  }
  async damageWaiversLazy(): Promise<Array<DamageWaiverStaff>> {
    if (!this.damageWaiversLoaded) {
      this.damageWaiversCached = await this.cbiServiceContext!.personService().getPersonStaffDamageWaivers(this.personId);
      this.damageWaiversLoaded = true;
    }
    return this.damageWaiversCached!;
  }
  async phonesLazy(): Promise<Array<PersonPhoneStaff>> {
    if (!this.phonesLoaded) {
      this.phonesCached = await this.cbiServiceContext!.personService().getPersonStaffPhones(this.personId);
      this.phonesLoaded = true;
    }
    return this.phonesCached!;
  }
  async personRatingsLazy(): Promise<Array<PersonRatingStaff>> {
    if (!this.personRatingsLoaded) {
      this.personRatingsCached = await this.cbiServiceContext!.personService().getPersonStaffPersonRatings(this.personId);
      this.personRatingsLoaded = true;
    }
    return this.personRatingsCached!;
  }
  async membershipsLazy(): Promise<Array<MembershipStaff>> {
    if (!this.membershipsLoaded) {
      this.membershipsCached = await this.cbiServiceContext!.personService().getPersonStaffMemberships(this.personId);
      this.membershipsLoaded = true;
    }
    return this.membershipsCached!;
  }
  async addressesLazy(): Promise<Array<PersonAddressStaff>> {
    if (!this.addressesLoaded) {
      this.addressesCached = await this.cbiServiceContext!.personService().getPersonStaffAddresses(this.personId);
      this.addressesLoaded = true;
    }
    return this.addressesCached!;
  }
  async cardsLazy(): Promise<Array<CardStaff>> {
    if (!this.cardsLoaded) {
      this.cardsCached = await this.cbiServiceContext!.personService().getPersonStaffCards(this.personId);
      this.cardsLoaded = true;
    }
    return this.cardsCached!;
  }
  async personTypesLazy(): Promise<Array<PersonPersonTypeStaff>> {
    if (!this.personTypesLoaded) {
      this.personTypesCached = await this.cbiServiceContext!.personService().getPersonStaffPersonTypes(this.personId);
      this.personTypesLoaded = true;
    }
    return this.personTypesCached!;
  }
  async apClassesInstructingLazy(): Promise<Array<ApClassStaff>> {
    if (!this.apClassesInstructingLoaded) {
      this.apClassesInstructingCached = await this.cbiServiceContext!.personService().getPersonStaffApClassesInstructing(this.personId);
      this.apClassesInstructingLoaded = true;
    }
    return this.apClassesInstructingCached!;
  }
  async jpClassesInstructingLazy(): Promise<Array<JpClassStaff>> {
    if (!this.jpClassesInstructingLoaded) {
      this.jpClassesInstructingCached = await this.cbiServiceContext!.personService().getPersonStaffJpClassesInstructing(this.personId);
      this.jpClassesInstructingLoaded = true;
    }
    return this.jpClassesInstructingCached!;
  }
  async apClassSignupsLazy(): Promise<Array<ApClassSignupStaff>> {
    if (!this.apClassSignupsLoaded) {
      this.apClassSignupsCached = await this.cbiServiceContext!.personService().getPersonStaffApClassSignups(this.personId);
      this.apClassSignupsLoaded = true;
    }
    return this.apClassSignupsCached!;
  }
  async jpClassSignupsLazy(): Promise<Array<JpClassSignupStaff>> {
    if (!this.jpClassSignupsLoaded) {
      this.jpClassSignupsCached = await this.cbiServiceContext!.personService().getPersonStaffJpClassSignups(this.personId);
      this.jpClassSignupsLoaded = true;
    }
    return this.jpClassSignupsCached!;
  }
  async apClassTypeOverridesLazy(): Promise<Array<ApClassTypeOverrideStaff>> {
    if (!this.apClassTypeOverridesLoaded) {
      this.apClassTypeOverridesCached = await this.cbiServiceContext!.personService().getPersonStaffApClassTypeOverrides(this.personId);
      this.apClassTypeOverridesLoaded = true;
    }
    return this.apClassTypeOverridesCached!;
  }
  async jpClassTypeOverridesLazy(): Promise<Array<JpClassTypeOverrideStaff>> {
    if (!this.jpClassTypeOverridesLoaded) {
      this.jpClassTypeOverridesCached = await this.cbiServiceContext!.personService().getPersonStaffJpClassTypeOverrides(this.personId);
      this.jpClassTypeOverridesLoaded = true;
    }
    return this.jpClassTypeOverridesCached!;
  }
  async relationsALazy(): Promise<Array<PersonRelationStaff>> {
    if (!this.relationsALoaded) {
      this.relationsACached = await this.cbiServiceContext!.personService().getPersonStaffRelationsA(this.personId);
      this.relationsALoaded = true;
    }
    return this.relationsACached!;
  }
  async relationsBLazy(): Promise<Array<PersonRelationStaff>> {
    if (!this.relationsBLoaded) {
      this.relationsBCached = await this.cbiServiceContext!.personService().getPersonStaffRelationsB(this.personId);
      this.relationsBLoaded = true;
    }
    return this.relationsBCached!;
  }
  async checkInsLazy(): Promise<Array<PersonCheckInStaff>> {
    if (!this.checkInsLoaded) {
      this.checkInsCached = await this.cbiServiceContext!.personService().getPersonStaffCheckIns(this.personId);
      this.checkInsLoaded = true;
    }
    return this.checkInsCached!;
  }
  async getChildren(): Promise<Array<PersonStaff>> {
    return await this.cbiServiceContext!.personService().getChildrenStaff({ personId: this.personId });
  }
  async getEmergencyContacts(): Promise<Array<PersonStaff>> {
    return await this.cbiServiceContext!.personService().getEmergencyContactsStaff({ personId: this.personId });
  }
  async getPersonName(options: GetNameOptions): Promise<string> {
    return await this.cbiServiceContext!.personService().getPersonNameStaff({ ref: { personId: this.personId }, options: options });
  }
}
